/**
 * ActionRow — a secondary path offered inside a dialog's body, as a row: what
 * the situation is, a line of what happens, and the action named on its right.
 * A dialog keeps ONE main action in its footer; a second or third way on
 * ("Correct", "Undo") is a row, explained, instead of another footer button.
 * The whole row is the button.
 */
import type { ComponentType, ReactNode } from 'react';

import { cn } from '../lib/utils';
import { Button } from '../primitives/button';

export interface ActionRowProps {
  title: ReactNode;
  hint?: ReactNode;
  actionLabel: ReactNode;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function ActionRow({
  title,
  hint,
  actionLabel,
  icon: Icon,
  onClick,
  loading,
  disabled,
  className,
  'data-testid': testId,
}: ActionRowProps) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      loading={loading}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'h-auto w-full justify-between gap-4 rounded-lg px-4 py-3 text-left font-normal whitespace-normal',
        className
      )}
    >
      <span className="min-w-0 space-y-0.5">
        <span className="text-foreground block text-sm font-semibold">{title}</span>
        {hint ? <span className="text-muted-foreground block text-xs">{hint}</span> : null}
      </span>
      <span className="text-primary-accessible flex shrink-0 items-center gap-1.5 text-sm font-semibold">
        {actionLabel}
        <Icon className="size-4" aria-hidden="true" />
      </span>
    </Button>
  );
}
